/* cl-reviews.js — Cellova native review wall.

   Locale: read once from the mount's data-loc, set server-side from request.locale.iso_code.
   The renderer never guesses and never re-picks, so the wall cannot flicker between languages.

   No layout shift: the server renders the first SEED cards in the wall's default sort order and
   this script renders the same cards first (sortDefault mirrors the Python sort_key in
   scripts/reviews/03-build-reviews.py byte-for-byte), so the hydration swap is invisible.

   Arc rule: a scope below `min` renders nothing at all — the mount removes itself rather than
   showing an empty state. Kept in sync with the Liquid gate so a stale asset cannot leak a shell.
*/
(function () {
  'use strict';

  // The section renders the sprite once; cards reference it. Keeps both the generated
  // seed snippet and this bundle small, and keeps one copy of each path in the DOM.
  var STAR = '<svg><use href="#cl-rv-star"/></svg>';
  var CHECK = '<svg><use href="#cl-rv-check"/></svg>';

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // `.o` marks the empty half of a rating — same one-letter class the generated seed
  // markup uses, so a hydrated card is byte-identical to the card it replaces.
  // Both ratings are drawn as graphics, so each carries a visually-hidden text equivalent.
  // Without it every card is rating-less to a screen reader.
  function srRating(n, i18n) {
    return '<span class="cl-rv__sr">' + esc((i18n.rating_of || '%n%/5').replace('%n%', n)) + '</span>';
  }

  function stars(n, big, i18n) {
    var out = '';
    for (var i = 1; i <= 5; i++) {
      out += i <= n ? '<span>' + STAR + '</span>' : '<span class="o">' + STAR + '</span>';
    }
    return srRating(n, i18n) +
      '<span class="cl-rv__stars' + (big ? ' cl-rv__stars--lg' : '') + '" aria-hidden="true">' + out + '</span>';
  }

  function dots(n, i18n) {
    var out = '';
    for (var i = 1; i <= 5; i++) {
      out += i <= n ? '<span></span>' : '<span class="o"></span>';
    }
    return '<span class="cl-rv__dots" aria-hidden="true">' + out + '</span>' + srRating(n, i18n);
  }

  /* Mirrors sort_key() in 03-build-reviews.py. Any change here must change there too,
     or the server seed and the first client render stop agreeing and the wall shifts. */
  function sortDefault(a, b) {
    if (b.r !== a.r) return b.r - a.r;
    var la = Math.min(Math.floor(a.bg.length / 60), 3);
    var lb = Math.min(Math.floor(b.bg.length / 60), 3);
    if (lb !== la) return lb - la;
    var oa = a.d < '2000' ? 1 : 0;
    var ob = b.d < '2000' ? 1 : 0;
    if (oa !== ob) return oa - ob;
    var da = parseInt(a.d.replace(/-/g, ''), 10);
    var db = parseInt(b.d.replace(/-/g, ''), 10);
    if (db !== da) return db - da;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  }

  var SORTS = {
    best: sortDefault,
    recent: function (a, b) {
      return a.d === b.d ? (a.id < b.id ? -1 : 1) : a.d < b.d ? 1 : -1;
    },
    highest: function (a, b) {
      return b.r === a.r ? sortDefault(a, b) : b.r - a.r;
    },
    lowest: function (a, b) {
      return a.r === b.r ? sortDefault(a, b) : a.r - b.r;
    }
  };

  function relDate(iso, loc, i18n) {
    var then = new Date(iso + 'T00:00:00');
    if (isNaN(then)) return '';
    // FLOOR, not round — Liquid's `divided_by` on integers floors, and the seed cards
    // compute their date with it. Rounding here put the seed one month behind the
    // hydrated card on roughly half the cards.
    var months = Math.max(0, Math.floor((Date.now() - then.getTime()) / 2629800000));
    if (months < 1) return i18n.just_now;
    // Full patterns, not a number plus a suffix: Bulgarian puts the marker in front
    // ("преди 2 месеца"), English behind ("2 months ago"). Must match rel_date() in
    // 03-build-reviews.py exactly or every seed card visibly re-writes itself.
    if (months < 12) {
      return (months === 1 ? i18n.month_ago : i18n.months_ago).replace('%n%', months);
    }
    var years = Math.floor(months / 12);
    return (years === 1 ? i18n.year_ago : i18n.years_ago).replace('%n%', years);
  }

  function init(root) {
    var loc = root.getAttribute('data-loc') === 'en' ? 'en' : 'bg';
    var handle = root.getAttribute('data-handle');
    var min = parseInt(root.getAttribute('data-min'), 10) || 0;
    var perPage = parseInt(root.getAttribute('data-per'), 10) || 8;
    var showMedia = root.getAttribute('data-media') === 'true';
    // How many cards the server actually seeded. The first client render must match it
    // exactly, whatever per_page says, or hydration adds or drops cards under the reader.
    var seedCount = parseInt(root.getAttribute('data-seed'), 10) || perPage;
    var i18nNode = root.querySelector('[data-cl-rv-i18n]');
    var i18n = {};
    try {
      i18n = JSON.parse(i18nNode.textContent);
    } catch (e) {
      return;
    }
    // Axis labels ride in the shard so the section does not inline them twice; they are
    // filled in once the shard lands. Topic labels are server-rendered on the chips.
    var axes = [];

    var list = root.querySelector('[data-cl-rv-list]');
    var showing = root.querySelector('[data-cl-rv-showing]');
    var moreWrap = root.querySelector('[data-cl-rv-more-wrap]');
    var sortSel = root.querySelector('[data-cl-rv-sort]');

    var state = { all: [], rating: 0, topic: '', sort: 'best', page: 1 };

    function filtered() {
      return state.all.filter(function (r) {
        if (state.rating && r.r !== state.rating) return false;
        if (state.topic && (r.tp || []).indexOf(state.topic) === -1) return false;
        return true;
      });
    }

    function card(r) {
      var body = loc === 'en' ? r.en : r.bg;
      var author = loc === 'en' ? r.ae || r.a : r.a;
      var sub = '';
      for (var i = 0; i < axes.length; i++) {
        sub +=
          '<span class="cl-rv__sub">' + esc(axes[i].label) + dots((r.s || [])[i] || r.r, i18n) + '</span>';
      }
      var media = '';
      if (showMedia && r.ph && r.ph.length) {
        media =
          '<div class="cl-rv__media">' +
          r.ph
            .map(function (u) {
              return (
                '<button type="button" class="cl-rv__thumb" data-cl-rv-photo="' +
                esc(u) +
                '"><img src="' + esc(u) + '" alt="" loading="lazy" width="64" height="64"></button>'
              );
            })
            .join('') +
          '</div>';
      }
      return (
        '<article class="cl-rv__card">' +
        '<div class="cl-rv__card-top">' +
        stars(r.r, false, i18n) +
        '<span class="cl-rv__meta">' + esc(relDate(r.d, loc, i18n)) + '</span>' +
        '</div>' +
        '<p class="cl-rv__body">' + esc(body) + '</p>' +
        media +
        '<div class="cl-rv__subs">' + sub + '</div>' +
        '<div class="cl-rv__foot">' +
        '<span class="cl-rv__author">' + esc(author) + '</span>' +
        (r.v
          ? '<span class="cl-rv__verified">' + CHECK + esc(i18n.verified) + '</span>'
          : '') +
        '</div>' +
        '</article>'
      );
    }

    /* Numbered pages, not load-more (Viktor 2026-08-27). Windowed so a 200-review wall never
       prints 25 buttons: first, last, and a run around the current page with ellipses. */
    function pager(pageCount) {
      if (pageCount < 2) return '';
      var cur = state.page, out = [], i;
      var want = {1: 1};
      want[pageCount] = 1;
      for (i = cur - 1; i <= cur + 1; i++) if (i >= 1 && i <= pageCount) want[i] = 1;
      var pages = Object.keys(want).map(Number).sort(function (a, b) { return a - b; });
      var prev = 0;
      for (i = 0; i < pages.length; i++) {
        if (pages[i] - prev > 1) out.push('<span class="cl-rv__gap" aria-hidden="true">…</span>');
        out.push(
          '<button type="button" class="cl-rv__page' + (pages[i] === cur ? ' is-current' : '') + '"' +
          ' data-cl-rv-page="' + pages[i] + '"' + (pages[i] === cur ? ' aria-current="page"' : '') +
          '>' + pages[i] + '</button>'
        );
        prev = pages[i];
      }
      return out.join('');
    }

    function render() {
      var rows = filtered().slice().sort(SORTS[state.sort] || sortDefault);
      var pageCount = Math.max(1, Math.ceil(rows.length / perPage));
      if (state.page > pageCount) state.page = pageCount;
      var start = (state.page - 1) * perPage;
      var slice = rows.slice(start, start + perPage);
      list.innerHTML = slice.length
        ? slice.map(card).join('')
        : '<p class="cl-rv__empty">' + esc(i18n.empty) + '</p>';

      if (showing) {
        showing.textContent = i18n.showing
          .replace('%shown%', slice.length ? (start + 1) + '–' + (start + slice.length) : '0')
          .replace('%total%', rows.length);
      }
      if (moreWrap) {
        moreWrap.hidden = pageCount < 2;
        moreWrap.innerHTML = pager(pageCount);
      }
      root.querySelectorAll('[data-cl-rv-rating]').forEach(function (el) {
        var v = parseInt(el.getAttribute('data-cl-rv-rating'), 10);
        el.setAttribute('aria-pressed', String(v === state.rating));
      });
      root.querySelectorAll('[data-cl-rv-topic]').forEach(function (el) {
        el.setAttribute('aria-pressed', String(el.getAttribute('data-cl-rv-topic') === state.topic));
      });
    }

    root.addEventListener('click', function (e) {
      var rateEl = e.target.closest('[data-cl-rv-rating]');
      if (rateEl) {
        var v = parseInt(rateEl.getAttribute('data-cl-rv-rating'), 10);
        state.rating = state.rating === v ? 0 : v;
        state.page = 1;
        render();
        return;
      }
      var topEl = e.target.closest('[data-cl-rv-topic]');
      if (topEl) {
        var t = topEl.getAttribute('data-cl-rv-topic');
        state.topic = state.topic === t ? '' : t;
        state.page = 1;
        render();
        return;
      }
      var pg = e.target.closest('[data-cl-rv-page]');
      if (pg) {
        var n = parseInt(pg.getAttribute('data-cl-rv-page'), 10);
        if (n && n !== state.page) {
          state.page = n;
          render();
          /* Land on the top of the wall. scroll-margin-top on the anchor keeps it clear of the
             sticky header — declarative, so it can't drift out of sync with the header's height
             the way hand-computed arithmetic did. */
          var anchor = root.querySelector('[data-cl-rv-top]') || root;
          anchor.scrollIntoView({ block: 'start', behavior: 'auto' });
        }
        return;
      }
      var photo = e.target.closest('[data-cl-rv-photo]');
      if (photo) {
        var dlg = root.querySelector('[data-cl-rv-lightbox]');
        if (dlg) {
          dlg.querySelector('img').src = photo.getAttribute('data-cl-rv-photo');
          dlg.showModal();
        }
        return;
      }
      if (e.target.closest('[data-cl-rv-lightbox-close]')) {
        var d2 = root.querySelector('[data-cl-rv-lightbox]');
        if (d2) d2.close();
      }
      var toggle = e.target.closest('[data-cl-rv-form-toggle]');
      if (toggle) {
        var form = root.querySelector('[data-cl-rv-form]');
        if (form) {
          var open = form.hidden;
          form.hidden = !open;
          toggle.setAttribute('aria-expanded', String(open));
        }
      }
    });

    if (sortSel) {
      sortSel.addEventListener('change', function () {
        state.sort = sortSel.value;
        state.page = 1;
        render();
      });
    }

    fetch(root.getAttribute('data-src'), { credentials: 'same-origin' })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (data) {
        if (!data || !data.reviews) return;
        // Arc rule, enforced client-side too: never leave a shell behind.
        if (data.reviews.length < min) {
          root.remove();
          return;
        }
        var lb = data.labels || {};
        axes = (lb.axes || []).map(function (a) {
          return { key: a.key, label: a[loc] || a.bg };
        });
        state.all = data.reviews;
        render();
      })
      .catch(function () {
        /* leave the server-rendered seed cards standing */
      });

    root.setAttribute('data-cl-rv-ready', 'true');
  }

  function boot() {
    document.querySelectorAll('[data-cl-rv]:not([data-cl-rv-ready])').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  // Theme editor re-renders the section without a page load.
  document.addEventListener('shopify:section:load', boot);
})();
